// src/hooks/useOnlineStatus.ts
import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setOnlineStatus } from '../store/onlineStatusSlice';
import { RootState } from '../store';
import { checkInternet } from '../utils/helpers';

const useOnlineStatus = (pingInterval = 3000) => {
    const dispatch = useDispatch();
    const isOnline = useSelector((state: RootState) => state.onlineStatus.isOnline);

    // ✅ SSR/browser guard
    const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';

    const updateOnlineStatus = useCallback(async () => {
        // Si pas de navigateur (SSR) -> offline par défaut
        if (!isBrowser) {
            dispatch(setOnlineStatus(false));
            return;
        }

        if (navigator.onLine) {
            try {
                const status = await checkInternet();
                dispatch(setOnlineStatus(!!status));
            } catch {
                // En cas d’erreur de ping, on considère offline
                dispatch(setOnlineStatus(false));
            }
        } else {
            dispatch(setOnlineStatus(false));
        }
    }, [dispatch, isBrowser]);

    useEffect(() => {
        // État initial: offline jusqu’à vérif réelle
        if (!isBrowser) {
            dispatch(setOnlineStatus(false));
            return;
        }

        let cancelled = false;

        // Vérification initiale
        (async () => {
            await updateOnlineStatus();
            if (cancelled) return;
        })();

        // Intervalle de ping
        const intervalId = setInterval(() => {
            if (!cancelled) updateOnlineStatus();
        }, pingInterval);

        // Écouteurs d'événements
        window.addEventListener('online', updateOnlineStatus);
        window.addEventListener('offline', updateOnlineStatus);

        // Nettoyage
        return () => {
            cancelled = true;
            clearInterval(intervalId);
            window.removeEventListener('online', updateOnlineStatus);
            window.removeEventListener('offline', updateOnlineStatus);
        };
    }, [dispatch, isBrowser, updateOnlineStatus, pingInterval]);

    return isOnline;
};

export default useOnlineStatus;
